// Emergency Numbers → Overview. Where things stand and the one place to start a scan.
import { Link } from "react-router-dom";
import { Loader2, Plug, PlugZap, ShieldAlert, ShieldCheck, TriangleAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import ScanLauncher from "./ScanLauncher";
import { NUMBER_STATUS, NUMBER_STATUS_ORDER, PLATFORM_LABEL, Pill, STATUS, STATUS_ORDER, Stat, ago, clock } from "./ui";

const CONN = [
  ["uc_platform_core", "UC Platform · Core"],
  ["uc_platform_sas", "UC Platform · SAS"],
  ["bandwidth_emergency", "Bandwidth · API credential"],
  ["bandwidth_static", "Bandwidth · Static account"],
  ["bandwidth_dlr", "Bandwidth · DLR account"],
];

function Connections({ conns, platforms }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid="e911-connections">
      {CONN.map(([alias, label]) => {
        const c = conns?.[alias] || {};
        const wanted = !alias.startsWith("uc_platform_") || platforms.includes(alias.replace("uc_platform_", ""));
        const tone = c.configured ? "bg-emerald-50 text-emerald-800 border-emerald-200"
          : wanted ? "bg-amber-50 text-amber-800 border-amber-200" : "bg-slate-50 text-slate-500 border-slate-200";
        const state = c.configured ? (c.account ? c.account : "connected") : wanted ? (alias.startsWith("bandwidth_") && alias !== "bandwidth_emergency" ? "not set" : "not connected") : "not in scope";
        return (
          <span key={alias} data-testid={`e911-conn-${alias}`} title={c.base_url || "not configured"}
                className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium ${tone}`}>
            {c.configured ? <PlugZap className="h-3.5 w-3.5" /> : <Plug className="h-3.5 w-3.5" />}
            {label}
            <span className="font-normal opacity-80">· {state}</span>
            {c.on_token === false && <span className="font-normal text-amber-700">· not on the token</span>}
          </span>
        );
      })}
    </div>
  );
}

function RunSummary({ run, title, testid, onGoto }) {
  if (!run) {
    return (
      <Card className="px-4 py-3 text-sm text-slate-500" data-testid={testid}>
        <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{title}</div>
        <div className="mt-1">None yet.</div>
      </Card>
    );
  }
  const s = run.summary || {};
  return (
    <Card className="px-4 py-3" data-testid={testid}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{title}</div>
          <div className="mt-0.5 text-sm text-slate-800">
            {run.kind === "scan" ? `${run.scope} scan` : `${run.execute ? "Executed" : "Dry-ran"} ${run.domain}`}
            <span className="text-slate-400"> · {run.by} · {ago(run.finished_at || run.started_at)}</span>
          </div>
        </div>
        <Pill tone={run.status === "done" ? "bg-emerald-100 text-emerald-800" : run.status === "running" ? "bg-sky-100 text-sky-800" : "bg-rose-100 text-rose-800"}>
          {run.status}
        </Pill>
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5 text-[11px] text-slate-600">
        {run.kind === "scan" ? (
          <>
            <span>{s.assessed || 0} assessed</span>
            {STATUS_ORDER.filter((k) => s[k]).map((k) => <Pill key={k} tone={STATUS[k].tone}>{s[k]} {STATUS[k].label}</Pill>)}
            {!!s.fixed && <Pill tone="bg-emerald-100 text-emerald-800">{s.fixed} auto-fixed</Pill>}
            {!!s.fix_failed && <Pill tone="bg-rose-100 text-rose-800">{s.fix_failed} fix failed</Pill>}
          </>
        ) : (
          <>
            {"converted" in s && <span>{s.converted} converted · {s.failed} failed · {s.rolled_back} rolled back</span>}
            {s.result && <Pill>{s.result}</Pill>}
          </>
        )}
        {!!run.errors && <Pill tone="bg-rose-100 text-rose-800">{run.errors} errors</Pill>}
        {!!run.warnings && <Pill tone="bg-amber-100 text-amber-800">{run.warnings} warnings</Pill>}
      </div>
      <button type="button" onClick={() => onGoto("runs", { run_id: run.run_id })} data-testid={`${testid}-log`}
              className="mt-2 text-xs font-medium text-primary hover:underline">Open the log</button>
    </Card>
  );
}

export default function Overview({ data, canEdit, onGoto, onStarted }) {
  if (!data) return <Loader2 className="h-5 w-5 animate-spin text-slate-300" data-testid="e911-overview-loading" />;
  const platforms = data.settings?.platforms || ["core"];
  const conns = data.connections || {};
  const bwOff = !conns.bandwidth_static?.configured;
  const d = data.domains || {};
  const n = data.numbers || {};
  const totalDomains = Object.values(d).reduce((a, b) => a + b, 0);
  const totalNumbers = Object.values(n).reduce((a, b) => a + b, 0);
  const running = data.running;
  const prog = running?.progress || {};
  const pct = prog.total ? Math.round((prog.done / prog.total) * 100) : 0;
  const auto = !!data.settings?.auto_fix_enabled;

  return (
    <div className="space-y-5" data-testid="e911-overview">
      <Connections conns={conns} platforms={platforms} />

      {bwOff && (
        <Card className="flex flex-wrap items-center justify-between gap-4 border-amber-200 bg-amber-50/60 px-4 py-3"
              data-testid="e911-bandwidth-warning">
          <div className="flex items-start gap-2 text-sm text-amber-900">
            <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              <strong>Bandwidth is not connected.</strong> Static registrations read as <em>unknown</em>, so every
              number shows as not registered, no deletion can be planned on a SIP trunk domain, and nothing can be
              auto-fixed. {conns.bandwidth_emergency?.configured
                ? "The credential is in place — set the static and DLR account numbers under Settings → Emergency Numbers, then run a scan."
                : "Paste the OAuth 2.0 Client ID + Client Secret (Bandwidth App → Account → API Credentials) under Settings → API Connections, set the account numbers under Settings → Emergency Numbers, then run a scan."}
            </span>
          </div>
          <Link to={conns.bandwidth_emergency?.configured ? "/settings?tab=e911" : "/settings?tab=api-connections"} data-testid="e911-goto-api-connections">
            <Button size="sm" variant="outline">{conns.bandwidth_emergency?.configured ? "Set the accounts" : "Connect Bandwidth"}</Button>
          </Link>
        </Card>
      )}

      {running && (
        <Card className="space-y-2 border-sky-200 bg-sky-50/60 px-4 py-3" data-testid="e911-running">
          <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-sky-900">
            <span className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              <strong>{running.kind === "scan" ? "Scan" : "Fix"} in progress</strong>
              <span className="text-sky-700">· {running.stage}</span>
            </span>
            <button type="button" onClick={() => onGoto("runs", { run_id: running.run_id })} data-testid="e911-running-log"
                    className="text-xs font-medium text-primary hover:underline">Watch the log</button>
          </div>
          {!!prog.total && (
            <div className="h-1.5 w-full overflow-hidden rounded bg-sky-100">
              <div className="h-full bg-sky-500 transition-[width]" style={{ width: `${pct}%` }} data-testid="e911-running-bar" />
            </div>
          )}
          {!!prog.total && <div className="text-[11px] text-sky-700">{prog.done} of {prog.total} domains · {pct}%</div>}
        </Card>
      )}

      <div>
        <div className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
          Domains · {totalDomains.toLocaleString()} assessed
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-8" data-testid="e911-domain-stats">
          {STATUS_ORDER.map((k) => (
            <Stat key={k} label={STATUS[k].label} value={(d[k] || 0).toLocaleString()} hint={STATUS[k].hint}
                  tone={d[k] ? (k === "blocked" || k === "error" ? "text-rose-700" : k === "needs_work" ? "text-amber-700"
                    : k === "address_missing" ? "text-orange-700"
                    : k === "review" ? "text-sky-700" : k === "correct" ? "text-emerald-700" : "text-slate-800") : "text-slate-400"}
                  testid={`e911-stat-${k}`} onClick={() => onGoto("domains", { status: k })} />
          ))}
        </div>
      </div>

      <div>
        <div className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
          Numbers · {totalNumbers.toLocaleString()} in the report
          {data.settings?.numbers_built_at && (
            <span className="font-normal normal-case tracking-normal text-slate-400">
              {" "}· built {Object.entries(data.settings.numbers_built_at).map(([p, at]) => `${PLATFORM_LABEL[p] || p} ${clock(at)}`).join(", ")} UTC
            </span>
          )}
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5" data-testid="e911-number-stats">
          {NUMBER_STATUS_ORDER.map((k) => (
            <Stat key={k} label={NUMBER_STATUS[k].label} value={(n[k] || 0).toLocaleString()} hint={NUMBER_STATUS[k].hint}
                  tone={n[k] ? (k === "not_registered" ? "text-rose-700" : k === "wrong" ? "text-amber-700" : k === "unused" ? "text-sky-700"
                    : k === "registered" ? "text-emerald-700" : "text-slate-800") : "text-slate-400"}
                  testid={`e911-nstat-${k}`} onClick={() => onGoto("numbers", { status: k })} />
          ))}
        </div>
        {!totalNumbers && (
          <p className="mt-2 text-xs text-slate-500" data-testid="e911-numbers-unbuilt">
            The numbers report has not been built yet — tick "Rebuild the numbers report" on a scan below, or wait for
            the nightly sweep (04:30).
          </p>
        )}
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <ScanLauncher platforms={platforms} conns={conns} canEdit={canEdit} running={!!running} onStarted={onStarted} />
        </div>
        <div className="space-y-4">
          <RunSummary run={data.last_scan} title="Last scan" testid="e911-last-scan" onGoto={onGoto} />
          <RunSummary run={data.last_fix} title="Last fix" testid="e911-last-fix" onGoto={onGoto} />
          <Card className="px-4 py-3" data-testid="e911-automation">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Unattended</div>
            <div className={`mt-1 flex items-center gap-2 text-sm font-medium ${auto ? "text-rose-700" : "text-slate-800"}`}
                 data-testid="e911-autofix-state">
              {auto ? <ShieldAlert className="h-4 w-4" /> : <ShieldCheck className="h-4 w-4 text-emerald-600" />}
              Auto-fix is {auto ? "ON" : "OFF"}
            </div>
            <p className="mt-1 text-[12px] leading-relaxed text-slate-500">
              {auto
                ? "The cron applies plans that have real 911 work and not a single blocker. Trunk + users domains are never touched without a decision."
                : "The cron only assesses and alerts. Every change goes through a manager's approval here."}
            </p>
            <ul className="mt-2 space-y-0.5 text-[11px] text-slate-500">
              <li>Every 3 h — assess what changed{auto ? ", auto-fix" : ""}</li>
              <li>04:30 — sweep every domain, rebuild the numbers report</li>
              <li>07:00 — digest: unregistered numbers + domains missing a subscriber address{data.settings?.alerts_enabled === false ? " (off)" : ""}
                {" "}· {(data.settings?.alert_recipients || []).length || "no"} recipient{(data.settings?.alert_recipients || []).length === 1 ? "" : "s"}</li>
            </ul>
            {canEdit && (
              <Link to="/settings?tab=e911" className="mt-2 inline-block text-xs font-medium text-primary hover:underline"
                    data-testid="e911-automation-settings">Change in Settings</Link>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
